import React, {useMemo, useState} from "react";
import * as MuiIcons from "@mui/icons-material";
import {Box, Grid, IconButton, InputAdornment, Popover, TextField, Tooltip, Typography} from "@mui/material";
import {PICTO_CATALOG, getPicto} from "../../../../utils/pictoCatalog.js";

/**
 * Sélecteur d'icône Material (contrat extranet <-> mobile : la valeur stockée est
 * le NOM Material Icon, ex. "VpnKey", rendu à l'identique sur Android).
 *
 * - Sans recherche : les icônes recommandées du catalogue (libellés FR).
 * - Avec recherche : tout le set Material (variante Filled uniquement — les
 *   suffixes Outlined/Rounded/TwoTone/Sharp n'ont pas d'équivalent mobile).
 * Contrôlé : value (nom Material) + onChange(nom).
 */

// Noms Filled du set Material, calculés une fois (≈ 2 100 icônes).
const ALL_MATERIAL_NAMES = Object.keys(MuiIcons)
    .filter((k) => /^[A-Z]/.test(k) && !/(Outlined|Rounded|TwoTone|Sharp)$/.test(k))
    .sort();

const MAX_RESULTS = 96;

function PictoIcon({pictoKey, color, fontSize}) {
    const entry = getPicto(pictoKey);
    const Cmp = MuiIcons[entry.mui] || MuiIcons.Help;
    return <Cmp sx={{color: color || "inherit", fontSize}}/>;
}

/** Libellé affiché : celui du catalogue si présent, sinon le nom Material. */
function labelOf(name) {
    return PICTO_CATALOG.find((p) => p.key === name)?.label ?? name;
}

function searchIcons(query) {
    const q = query.trim().toLowerCase().replace(/\s+/g, "");
    if (!q) return PICTO_CATALOG.map((p) => p.key);
    const fromCatalog = PICTO_CATALOG
        .filter((p) => p.key.toLowerCase().includes(q) || p.label.toLowerCase().replace(/\s+/g, "").includes(q))
        .map((p) => p.key);
    const fromAll = ALL_MATERIAL_NAMES.filter((n) => n.toLowerCase().includes(q) && !fromCatalog.includes(n));
    return [...fromCatalog, ...fromAll].slice(0, MAX_RESULTS);
}

export default function IconPicker({value, color = "#11285e", onChange, label = "Icône", error, showLabel = true}) {
    const [anchorEl, setAnchorEl] = useState(null);
    const [query, setQuery] = useState("");
    const open = Boolean(anchorEl);

    const results = useMemo(() => searchIcons(query), [query]);
    const isSearching = query.trim() !== "";

    const close = () => {
        setAnchorEl(null);
        setQuery("");
    };

    return (
        <Box>
            {showLabel &&
                <Typography variant="caption" color={error ? "error" : "textSecondary"}>{label}</Typography>
            }
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5}}>
                <IconButton
                    onClick={(e) => setAnchorEl(e.currentTarget)}
                    sx={{
                        border: "1px solid",
                        borderColor: error ? "error.main" : "divider",
                        borderRadius: 2,
                        width: 56,
                        height: 56,
                    }}
                    aria-label="Choisir une icône"
                >
                    <PictoIcon pictoKey={value} color={color} fontSize={30}/>
                </IconButton>
                <Box>
                    <Typography variant="body2" color="textSecondary">{value ? labelOf(value) : "Aucune icône"}</Typography>
                    {value && <Typography variant="caption" color="textSecondary" sx={{fontFamily: "monospace"}}>{value}</Typography>}
                </Box>
            </Box>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={close}
                anchorOrigin={{vertical: "bottom", horizontal: "left"}}
            >
                <Box sx={{p: 1.5, width: 380}}>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        placeholder="Rechercher parmi toutes les icônes Material…"
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start"><MuiIcons.Search fontSize="small"/></InputAdornment>
                            ),
                        }}
                        sx={{mb: 1}}
                    />
                    <Typography variant="caption" color="textSecondary" sx={{display: "block", mb: 0.5}}>
                        {isSearching
                            ? `${results.length} résultat(s)${results.length === MAX_RESULTS ? " — affinez la recherche" : ""}`
                            : `Recommandées (${PICTO_CATALOG.length}) — tapez pour chercher parmi ${ALL_MATERIAL_NAMES.length} icônes`}
                    </Typography>
                    <Box sx={{maxHeight: 320, overflowY: "auto"}}>
                        {results.length === 0 &&
                            <Typography variant="body2" color="textSecondary" sx={{p: 2, textAlign: "center"}}>
                                Aucune icône ne correspond.
                            </Typography>
                        }
                        <Grid container spacing={0.5}>
                            {results.map((name) => (
                                <Grid item key={name}>
                                    <Tooltip title={isSearching ? `${labelOf(name)} (${name})` : labelOf(name)}>
                                        <IconButton
                                            onClick={() => {
                                                onChange(name);
                                                close();
                                            }}
                                            sx={{
                                                borderRadius: 2,
                                                bgcolor: value === name ? "action.selected" : "transparent",
                                            }}
                                        >
                                            <PictoIcon pictoKey={name} color={color} fontSize={26}/>
                                        </IconButton>
                                    </Tooltip>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                </Box>
            </Popover>
        </Box>
    );
}
